import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "now-inventory-edit-container" }
const _hoisted_2 = { class: "execution-date-label" }
const _hoisted_3 = { class: "inventory-badge__list" }
const _hoisted_4 = {
  key: 0,
  class: "inventory-badge__list"
}

import { computed, markRaw } from 'vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import NotificationBadge from '@/InventoryControlModule/components/NotificationBadge.vue';
import InventoryAlertCell from '@/InventoryControlModule/components/InventoryTable/InventoryAlertCell.vue';
import RecommendTag from '@/corePagesModule/components/RecommendTag.vue';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import type { IParentBusiness } from '@/InventoryControlModule/domains/entities/Business';
import type { TableType } from '@/InventoryControlModule/composables/useTableState';
import type { IMonthlyBusinessData } from '@/InventoryControlModule/domains/entities/MonthlyData';
import type { InventoryTableViewModel } from '@/InventoryControlModule/pages/inventoryTableViewModel';
import { useTableCalculation } from '@/InventoryControlModule/composables/useTableCalculation';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import { IEditTransactionValue } from '@/InventoryControlModule/pages/useInventoryStore/type';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 * InventoryMonthlyColumn コンポーネントのプロパティ
 */
interface Props {
  /** 対象の月 (相対月) */
  month: number;
  /** ビジネスアイテム */
  item: IParentBusiness;
  /** 仮想化アイテムタイプ */
  virtualItemType: 'parent' | 'child';
  /** 月次データ */
  monthlyData: Record<string, IMonthlyBusinessData>;
  /** 表示対象のヘッダーリスト */
  headerList: string[];
  /** 開いているビジネスID */
  isOpeningBusinessId: string;
  /** インベントリViewModel */
  inventoryVm: InventoryTableViewModel;
  /** 月次エンドヘッダー */
  monthlyEndHeader: string;
  /** ハイライトされているか */
  isHighlighted: boolean;
  /** 数値編集モードかどうか */
  isEditTransactionMode: boolean;
  /** 当該月の数値編集モードの値 */
  currentMonthEditTransactionValue?: IEditTransactionValue[];
  /** テーブル数値スタイル関数 */
  tableNumberStyleClass: (business: IParentBusiness, currentOpenId?: string, tableType?: TableType) => string;
  /** 在庫ステータススタイル関数 */
  getNowInventoryStatusClass: (business: IParentBusiness, tableType?: TableType) => string;
  /** 実行日フォーマット関数 */
  getExecutionDateFormatted: (business: IParentBusiness) => string;
  /** 負の値判定関数 */
  isNegative: (value?: number) => boolean;
  /** 近接推奨チェック関数 */
  checkNearestRecommend: (monthlyData: Record<string, IMonthlyBusinessData>, businessId: string, month: number) => boolean;
  /** 到着ステータス取得関数 */
  getArrivalStatusWhenDisplayButton: (
    monthlyData: Record<string, IMonthlyBusinessData>,
    businessId: string,
    month: number
  ) => ArrivalStatus;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'InventoryMonthlyColumn',
  props: {
    month: {},
    item: {},
    virtualItemType: {},
    monthlyData: {},
    headerList: {},
    isOpeningBusinessId: {},
    inventoryVm: {},
    monthlyEndHeader: {},
    isHighlighted: { type: Boolean },
    isEditTransactionMode: { type: Boolean },
    currentMonthEditTransactionValue: {},
    tableNumberStyleClass: { type: Function },
    getNowInventoryStatusClass: { type: Function },
    getExecutionDateFormatted: { type: Function },
    isNegative: { type: Function },
    checkNearestRecommend: { type: Function },
    getArrivalStatusWhenDisplayButton: { type: Function }
  },
  emits: ["updateEditTransactionValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

/**
 * 最適化されたテーブル計算Composable
 * WeakMapキャッシュによる月次データ計算の高速化
 */
const { getMonthlyData } = useTableCalculation();

/**
 * キャッシュされた月次データ
 */
const cachedMonthlyData = computed(() => {
  return getMonthlyData(props.monthlyData, props.item.businessId, props.month);
});

// Recommendedが含まれるかどうか
const hasRecommended = computed(() => {
  const list = cachedMonthlyData.value?.arrivalStatusList;
  return Array.isArray(list) && list.includes(ArrivalStatus.Recommended);
});

/**
 * 月次トランザクションキーのリスト
 * @description 対象月の全トランザクションキーを取得
 */
const monthlyTransactionKeys = computed(() => {
  const keys = props.inventoryVm.getMonthlyTransactionHeaderKeys(props.month, false);
  // 表示対象のキーのみをフィルタリングして返す
  return keys.filter((key) => props.headerList.includes(`transaction-${props.month}-${key}`));
});

/**
 * スタイルクラスの計算
 */
const tableNumberStyle = computed(() => {
  return props.tableNumberStyleClass(props.item, props.isOpeningBusinessId, 'virtualized');
});

const inventoryStatusStyle = computed(() => {
  return props.getNowInventoryStatusClass(props.item, 'virtualized');
});

/**
 * 組み合わせスタイルクラス
 */
const combinedStyles = computed(() => {
  return [tableNumberStyle.value, inventoryStatusStyle.value];
});

/**
 * 親タイプの判定
 */
const isParent = computed(() => props.virtualItemType === 'parent');

/**
 * 実行日フォーマット済み文字列
 */
const formattedExecutionDate = computed(() => {
  return isParent.value ? props.getExecutionDateFormatted(props.item) : '';
});

/**
 * 推奨チェック結果
 */
const nearestRecommendCheck = computed(() => {
  return isParent.value ? props.checkNearestRecommend(props.monthlyData, props.item.businessId, props.month) : false;
});

/**
 * 到着ステータ
 */
const arrivalStatus = computed(() => {
  return isParent.value ? props.getArrivalStatusWhenDisplayButton(props.monthlyData, props.item.businessId, props.month) : null;
});

const isEditTransactionInput = computed(() => {
  if (!props.isEditTransactionMode) return false;
  return {
    arrival: props.virtualItemType === 'parent' && props.month < 0,
    endInventory: props.virtualItemType === 'parent' && props.month < 0,
    announce: props.virtualItemType === 'child',
    supplierAnnounce: props.virtualItemType === 'parent' && props.month >= 0,
    salesResult: props.virtualItemType === 'child' && props.month < 0,
    nowInventory: props.virtualItemType === 'parent' && props.month === 0,
  };
});

/**
 * 数値編集inputのmodelValueを取得
 * @param transactionKey - 数値列キー
 */
const getEditTransactionInputModelValue = (transactionKey: string): number => {
  // supplierAnnounceはAPIのtransactionTypeがsupplierForecast
  // nowInventoryはAPIのtransactionTypeがcurrentInventory
  const searchType =
    transactionKey === 'supplierAnnounce' ? 'supplierForecast' : transactionKey === 'nowInventory' ? 'currentInventory' : transactionKey;
  const foundValue = props.currentMonthEditTransactionValue?.find(
    (value) => value.type === searchType && (props.virtualItemType === 'child' ? value.baseBusinessId === props.item.businessId : true)
  );
  return foundValue?.value ?? cachedMonthlyData.value[transactionKey] ?? 0;
};

/**
 * 数値編集のinputイベントハンドラ
 * @param val - 変更後の値
 * @param transactionKey - 数値列キー
 */
const inputEditTransactionHandler = (val: string, transactionKey: string) => {
  const originValue = cachedMonthlyData.value[transactionKey] ?? 0;

  const editTransactionValueObj = {
    // supplierAnnounceはAPIのtransactionTypeがsupplierForecast
    // nowInventoryはAPIのtransactionTypeがcurrentInventory
    type:
      transactionKey === 'supplierAnnounce' ? 'supplierForecast' : transactionKey === 'nowInventory' ? 'currentInventory' : transactionKey,
    month: props.month,
    value: parseFloat(val),
    originValue: originValue,
  };
  if (props.virtualItemType === 'child') {
    editTransactionValueObj['baseBusinessId'] = props.item.businessId;
    if (transactionKey === 'announce') {
      const receivedYearMonth = props.item.customerAnnounce?.find((v) => {
        const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month);
        return v.year === targetYearMonth.year && v.month === targetYearMonth.month;
      })?.manageInitiatedYearMonth;
      if (receivedYearMonth) {
        editTransactionValueObj['receivedYearMonth'] = receivedYearMonth;
      } else if (props.month > 0) {
        // 新規作成かつ編集対象年月が翌月以降の場合、操作日の前月を受領月とする
        editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(-1);
      } else {
        // 新規作成かつ編集対象年月が当月以前の場合、編集対象年月の前月を受領月とする
        editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month - 1);
      }
    }
  } else if (transactionKey === 'supplierAnnounce') {
    // 親取引の仕入先内示は作成年月を送る（フィールドは受領年月と共用）
    const createdYearMonth = (props.item as any).supplierAnnounce?.find((v: any) => {
      const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month);
      return v.year === targetYearMonth.year && v.month === targetYearMonth.month;
    })?.manageInitiatedYearMonth;
    if (createdYearMonth) {
      editTransactionValueObj['receivedYearMonth'] = createdYearMonth;
    } else if (props.month > 0) {
      // 新規作成かつ編集対象年月が翌月以降の場合、操作日の前月を受領月とする
      editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(-1);
    } else {
      // 新規作成かつ編集対象年月が当月以前の場合、編集対象年月の前月を作成月とする(仕入先内示で該当するのは当月のみの想定)
      editTransactionValueObj['receivedYearMonth'] = DateService.getShiftedIYearMonthFromNowDateByMonths(props.month - 1);
    }
  } else if (transactionKey === 'nowInventory') {
    // 現在庫の場合はexecutionDateの年月を送る
    if (props.item.executionDate) {
      editTransactionValueObj['executionDate'] = props.item.executionDate;
    }
  }
  emits('updateEditTransactionValue', editTransactionValueObj);
  return;
};

/**
 * 子取引の情報を変更した際に変わる集計の値
 */
const getChangedAggregateBusinessTransactionValue = computed(() => {
  return {
    salesResult:
      cachedMonthlyData.value.salesResult +
      (props.currentMonthEditTransactionValue?.reduce((acc: number, val: IEditTransactionValue) => {
        return val.type === 'salesResult' ? acc + val.value - val.originValue : acc;
      }, 0) ?? 0),
    announce:
      cachedMonthlyData.value.announce +
      (props.currentMonthEditTransactionValue?.reduce((acc: number, val: IEditTransactionValue) => {
        return val.type === 'announce' ? acc + val.value - val.originValue : acc;
      }, 0) ?? 0),
  };
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(monthlyTransactionKeys.value, (transactionKey) => {
    return (_openBlock(), _createElementBlock(_Fragment, { key: transactionKey }, [
      (transactionKey === 'nowInventory' && isEditTransactionInput.value['nowInventory'])
        ? (_openBlock(), _createBlock(EzCell, {
            key: 0,
            "is-number": "",
            "is-highlighted": "",
            class: _normalizeClass(combinedStyles.value),
            "has-right-border": transactionKey === _ctx.monthlyEndHeader
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createVNode(NumberInputWithComma, {
                  class: "table-input now-inventory-input",
                  "model-value": getEditTransactionInputModelValue('nowInventory'),
                  onInput: _cache[0] || (_cache[0] = (e) => inputEditTransactionHandler(e, 'nowInventory'))
                }, null, 8, ["model-value"]),
                _createElementVNode("span", _hoisted_2, "(" + _toDisplayString(formattedExecutionDate.value) + ")", 1)
              ])
            ], undefined, true),
            _: 2
          }, 1032, ["class", "has-right-border"]))
        : (transactionKey === 'nowInventory')
          ? (_openBlock(), _createBlock(EzCell, {
              key: 1,
              "is-number": true,
              "is-negative": _ctx.isNegative(cachedMonthlyData.value.nowInventory),
              "is-highlighted": _ctx.isHighlighted,
              class: _normalizeClass(combinedStyles.value),
              "has-right-border": transactionKey === _ctx.monthlyEndHeader
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(isParent.value ? `${_ctx.item.inventory?.roundToDecimal(2).addComma() ?? 0} (${formattedExecutionDate.value})` : '--'), 1)
              ], undefined, true),
              _: 2
            }, 1032, ["is-negative", "is-highlighted", "class", "has-right-border"]))
          : (transactionKey === 'expectedArrival')
            ? (_openBlock(), _createBlock(EzCell, {
                key: 2,
                "is-number": true,
                "is-negative": _ctx.isNegative(cachedMonthlyData.value.exceptedArrivalForNormalMode),
                "is-highlighted": _ctx.isHighlighted,
                class: _normalizeClass(tableNumberStyle.value),
                "has-right-border": transactionKey === _ctx.monthlyEndHeader
              }, {
                default: _withCtx(() => [
                  (!isParent.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(" -- ")
                      ], 64))
                    : (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: _normalizeClass(["inventory-badge__container", tableNumberStyle.value])
                      }, [
                        (hasRecommended.value)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createVNode(_component_router_link, {
                                to: { name: 'recommend-order', params: { id: _ctx.item.businessId } },
                                target: "_blank"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(RecommendTag, {
                                    status: _unref(ArrivalStatus).Recommended
                                  }, null, 8, ["status"])
                                ], undefined, true),
                                _: 1
                              }, 8, ["to"]),
                              _createElementVNode("div", _hoisted_3, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cachedMonthlyData.value.arrivalStatusList, (arrivalStatusItem) => {
                                  return (_openBlock(), _createElementBlock(_Fragment, {
                                    key: `${_ctx.item.businessId}-${arrivalStatusItem}`
                                  }, [
                                    (arrivalStatusItem !== _unref(ArrivalStatus).Recommended && arrivalStatusItem !== _unref(ArrivalStatus).Nothing)
                                      ? (_openBlock(), _createBlock(NotificationBadge, {
                                          key: 0,
                                          class: "inventory-badge",
                                          style: _normalizeStyle(_ctx.inventoryVm.notificationBadgeStyle(arrivalStatusItem, false)),
                                          "arrival-status": arrivalStatusItem,
                                          unit: _ctx.item.unit,
                                          "un-paid-arrivals": cachedMonthlyData.value.unPaidArrival,
                                          "approval-requested-arrival": cachedMonthlyData.value.approvalRequestedArrival
                                        }, null, 8, ["style", "arrival-status", "unit", "un-paid-arrivals", "approval-requested-arrival"]))
                                      : _createCommentVNode("", true)
                                  ], 64))
                                }), 128))
                              ])
                            ], 64))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              ((cachedMonthlyData.value.exceptedArrivalForNormalMode ?? 0) > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cachedMonthlyData.value.arrivalStatusList, (arrivalStatusItem) => {
                                      return (_openBlock(), _createElementBlock(_Fragment, {
                                        key: `${_ctx.item.businessId}-${arrivalStatusItem}`
                                      }, [
                                        (arrivalStatusItem !== _unref(ArrivalStatus).Nothing)
                                          ? (_openBlock(), _createBlock(NotificationBadge, {
                                              key: 0,
                                              class: "inventory-badge",
                                              style: _normalizeStyle(_ctx.inventoryVm.notificationBadgeStyle(arrivalStatusItem, false)),
                                              "arrival-status": arrivalStatusItem,
                                              unit: _ctx.item.unit,
                                              "un-paid-arrivals": cachedMonthlyData.value.unPaidArrival,
                                              "approval-requested-arrival": cachedMonthlyData.value.approvalRequestedArrival
                                            }, null, 8, ["style", "arrival-status", "unit", "un-paid-arrivals", "approval-requested-arrival"]))
                                          : _createCommentVNode("", true)
                                      ], 64))
                                    }), 128))
                                  ]))
                                : _createCommentVNode("", true),
                              _createTextVNode(" " + _toDisplayString(cachedMonthlyData.value.exceptedArrivalForNormalMode?.roundToDecimal(2).addComma()), 1)
                            ], 64))
                      ], 2))
                ], undefined, true),
                _: 2
              }, 1032, ["is-negative", "is-highlighted", "class", "has-right-border"]))
            : (transactionKey === 'expectedEndInventory')
              ? (_openBlock(), _createBlock(EzCell, {
                  key: 3,
                  "is-number": true,
                  "is-negative": isParent.value && _ctx.isNegative(cachedMonthlyData.value.exceptedEndInventory),
                  "is-highlighted": _ctx.isHighlighted,
                  class: _normalizeClass(tableNumberStyle.value),
                  "has-right-border": transactionKey === _ctx.monthlyEndHeader
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(isParent.value ? cachedMonthlyData.value.exceptedEndInventory?.roundToDecimal(2).addComma() : '--'), 1)
                  ], undefined, true),
                  _: 2
                }, 1032, ["is-negative", "is-highlighted", "class", "has-right-border"]))
              : (transactionKey === 'alert')
                ? (_openBlock(), _createBlock(EzCell, {
                    key: 4,
                    "is-number": true,
                    "is-negative": _ctx.isNegative(cachedMonthlyData.value[transactionKey]),
                    "is-highlighted": _ctx.isHighlighted,
                    class: _normalizeClass(tableNumberStyle.value),
                    "has-right-border": transactionKey === _ctx.monthlyEndHeader
                  }, {
                    default: _withCtx(() => [
                      _createVNode(InventoryAlertCell, {
                        "prop-business": _ctx.item,
                        "prop-relative-month": _ctx.month
                      }, null, 8, ["prop-business", "prop-relative-month"])
                    ], undefined, true),
                    _: 2
                  }, 1032, ["is-negative", "is-highlighted", "class", "has-right-border"]))
                : (isEditTransactionInput.value[transactionKey])
                  ? (_openBlock(), _createBlock(EzCell, {
                      key: 5,
                      "is-number": "",
                      "is-highlighted": "",
                      "has-right-border": transactionKey === _ctx.monthlyEndHeader
                    }, {
                      default: _withCtx(() => [
                        _createVNode(NumberInputWithComma, {
                          class: "table-input",
                          "model-value": getEditTransactionInputModelValue(transactionKey),
                          onInput: (e) => inputEditTransactionHandler(e, transactionKey)
                        }, null, 8, ["model-value", "onInput"])
                      ], undefined, true),
                      _: 2
                    }, 1032, ["has-right-border"]))
                  : (
        _ctx.isEditTransactionMode &&
        _ctx.virtualItemType === 'parent' &&
        (transactionKey === 'announce' || (_ctx.month < 0 && transactionKey === 'salesResult')) &&
        getChangedAggregateBusinessTransactionValue.value[transactionKey] !== cachedMonthlyData.value[transactionKey]
      )
                    ? (_openBlock(), _createBlock(EzCell, {
                        key: 6,
                        "is-number": "",
                        "is-negative": isParent.value && _ctx.isNegative(cachedMonthlyData.value[transactionKey]),
                        "is-highlighted": _ctx.isHighlighted,
                        class: _normalizeClass(tableNumberStyle.value),
                        "has-right-border": transactionKey === _ctx.monthlyEndHeader
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(`${cachedMonthlyData.value[transactionKey]?.roundToDecimal(2).addComma() ?? 0} → ${
          getChangedAggregateBusinessTransactionValue.value[transactionKey].roundToDecimal(2).addComma() ?? 0
        } `), 1)
                        ], undefined, true),
                        _: 2
                      }, 1032, ["is-negative", "is-highlighted", "class", "has-right-border"]))
                    : (_openBlock(), _createBlock(EzCell, {
                        key: 7,
                        "is-number": "",
                        "is-negative": isParent.value && _ctx.isNegative(cachedMonthlyData.value[transactionKey]),
                        "is-highlighted": _ctx.isHighlighted,
                        class: _normalizeClass(tableNumberStyle.value),
                        "has-right-border": transactionKey === _ctx.monthlyEndHeader
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(isParent.value || transactionKey === 'announce' || transactionKey === 'salesResult'
          ? cachedMonthlyData.value[transactionKey]?.roundToDecimal(2).addComma() ?? 0
          : '--'), 1)
                        ], undefined, true),
                        _: 2
                      }, 1032, ["is-negative", "is-highlighted", "class", "has-right-border"]))
    ], 64))
  }), 128))
}
}

})